/* eslint-disable complexity */
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { InferValueTypes } from '~/src/store';

import { getServicesBySuspendStatus } from './selectors/helpers/getServicesBySuspendStatus';
import { getServicesByType } from './selectors/helpers/getServicesByType';
import * as actionCreators from './actions';
import { MIN_SUSPEND } from './constants';
import { EServiceType, EStages, IState } from './types';

export type { IState } from './types';

export const actionTypes = {
    INITIALIZED: '@LK/SUSPEND/INITIALIZED',
    SET_LOADING: '@LK/SUSPEND/SET_LOADING',
    SET_ERROR: '@LK/SUSPEND/SET_ERROR',
    TOGGLE_CHECKED: '@LK/SUSPEND/TOGGLE_CHECKED',
    TOGGLE_SHOW_SUSPENDED: '@LK/SUSPEND/TOGGLE_SHOW_SUSPENDED',
    TOGGLE_SELECT_ALL: '@LK/SUSPEND/TOGGLE_SELECT_ALL',
    SET_SUSPEND_FROM: '@LK/SUSPEND/SET_SUSPEND_FROM',
    SET_SUSPEND_TO: '@LK/SUSPEND/SET_SUSPEND_TO',
    SET_STAGE: '@LK/SUSPEND/SET_STAGE',
    SET_PRICES: '@LK/SUSPEND/SET_PRICES',
    SET_RESULT_SUCCESS: '@LK/SUSPEND/SET_RESULT_SUCCESS',
    SET_CHECKED_ALL_INCLUDED: '@LK/SUSPEND/SET_CHECKED_ALL_INCLUDED',
    SET_DAYS_TO_UNSUSPEND_TV: '@LK/SUSPEND/SET_DAYS_TO_UNSUSPEND_TV',
    RESET: '@LK/SUSPEND/RESET',
} as const;

export type TAction = ReturnType<InferValueTypes<typeof actionCreators>>;

export const initState: IState = {
    isLoading: false,
    errorMessage: '',
    successResult: false,
    currentStage: EStages.init,
    showSuspended: false,
    services: {},
    suspendFrom: '',
    suspendTo: '',
    suspendPrice: 0,
    recommendPay: 0,
    remainingPay: 0,
    balanceAfterSuspend: 0,
    daysToUnsuspendTv: 0,
};

export const reducer = (state: Readonly<IState> = initState, action: TAction): IState => {
    switch (action.type) {
        case actionTypes.INITIALIZED: {
            const services = action.payload.reduce<IState['services']>((acc, curr, index) => {
                acc[curr.id] = { ...curr, sortIdx: index };
                return acc;
            }, {});

            return {
                ...state,
                services,
                isLoading: false,
                errorMessage: '',
            };
        }
        case actionTypes.SET_LOADING: {
            return {
                ...state,
                isLoading: true,
            };
        }
        case actionTypes.SET_ERROR: {
            return {
                ...state,
                errorMessage: action.payload,
                isLoading: false,
            };
        }
        case actionTypes.TOGGLE_CHECKED: {
            const newServices = cloneDeep(state.services);
            newServices[action.payload] = {
                ...newServices[action.payload],
                isChecked: !newServices[action.payload].isChecked,
            };

            return {
                ...state,
                services: newServices,
            };
        }
        case actionTypes.SET_CHECKED_ALL_INCLUDED: {
            if (!state.showSuspended) {
                return state;
            }

            const newServices = cloneDeep(state.services);
            const suspendedServices = getServicesBySuspendStatus(Object.values(newServices), true);

            const allMainChecked = getServicesByType(suspendedServices, EServiceType.main).every(el => el.isChecked);
            if (!allMainChecked) {
                return state;
            }

            const includedNotChecked = getServicesByType(suspendedServices, EServiceType.included).filter(el => !el.isChecked);
            if (!includedNotChecked) {
                return state;
            }

            includedNotChecked.forEach(service => {
                service.isChecked = true;
            });
            return {
                ...state,
                services: newServices,
            };
        }
        case actionTypes.TOGGLE_SHOW_SUSPENDED: {
            return {
                ...state,
                showSuspended: !state.showSuspended,
            };
        }
        case actionTypes.TOGGLE_SELECT_ALL: {
            const isSelect = action.payload;
            const newServices = cloneDeep(state.services);
            const servicesBySuspendStatus = Object.values(newServices).filter(el => state.showSuspended ? el.isSuspended : !el.isSuspended);
            servicesBySuspendStatus.forEach(service => {
                service.isChecked = isSelect;
            });

            return {
                ...state,
                services: newServices,
            };
        }
        case actionTypes.SET_SUSPEND_FROM: {
            const suspendTo = state.suspendTo ? dayjs(state.suspendTo, 'YYYY-MM-DD') : '';
            const diff = (suspendTo && action.payload) ? (suspendTo.diff(dayjs(action.payload, 'YYYY-MM-DD'), 'day')) : MIN_SUSPEND;

            return {
                ...state,
                suspendFrom: action.payload,
                suspendTo: diff < MIN_SUSPEND ? '' : state.suspendTo,
            };
        }
        case actionTypes.SET_SUSPEND_TO: {
            return {
                ...state,
                suspendTo: action.payload,
            };
        }
        case actionTypes.SET_STAGE: {
            return {
                ...state,
                currentStage: action.payload,
            };
        }
        case actionTypes.SET_PRICES: {
            return {
                ...state,
                ...action.payload,
                isLoading: false,
                errorMessage: '',
            };
        }
        case actionTypes.SET_RESULT_SUCCESS: {
            return {
                ...state,
                successResult: true,
            };
        }
        case actionTypes.SET_DAYS_TO_UNSUSPEND_TV: {
            return {
                ...state,
                daysToUnsuspendTv: action.payload,
            };
        }
        case actionTypes.RESET: {
            return initState;
        }
        default: {
            const neverAction: never = action;
            return state || neverAction;
        }
    }
};
